import { useState, useEffect } from "react"
import { isEmpty, map } from "lodash"
import {
  Paper,
  Typography,
  CircularProgress,
  Divider,
  Alert,
} from "@mui/material"
import AIResultsTable from "./components/ai-results-table"
import AIInputForms from "./components/ai-input-forms"
import ExcelDownload from "components/excel-download"
import Loading from "components/loading"
import { usePortfolios } from "resources/portfolios"
import { useModels } from "resources/models"
import { useAIQuickAnswers } from "hooks/ai-quick-answers"
import { OK, LOADING, ERROR, combineTanStackStatuses } from "helpers/data-statuses"
import { track } from "helpers/analytics"
import { TRACKING_EVENTS } from "./constants"
import { AI_DISCLAIMER } from "helpers/ai"
import "./style.scss"

export default function AIQuickAnswers() {
  const [portfolios, portfoliosStatus] = usePortfolios()
  const [models, modelsStatus] = useModels()

  const [queryParams, setQueryParams] = useState({
    question: "",
    modelId: "",
    resourceId: "",
    type: "",
  })

  const [shouldRunQuery, setShouldRunQuery] = useState(false)

  // Store the last successful query results and question
  const [lastResults, setLastResults] = useState(null)
  const [lastQuestion, setLastQuestion] = useState("")
  // Save the last successful formatted data for download
  const [lastFormattedData, setLastFormattedData] = useState([])

  // Use the usePostQuery hook for AI queries
  const [results, queryStatus] = useAIQuickAnswers({
    ...queryParams,
    base: [],
    enabled: shouldRunQuery, // Only run the query when triggered by button click
  })

  const hasData = !isEmpty(results?.records)

  // Update lastResults when we get successful results
  useEffect(() => {
    if (queryStatus === OK && hasData) {
      setLastResults(results)

      // Also precompute and save the formatted data for download
      const headers = Object.keys(results.records[0])
      const formattedData = [
        headers,
        ...map(results.records, record =>
          map(headers, header => {
            const value = record[header]
            return value === null || value === undefined ? "" : value
          }),
        ),
      ]
      setLastFormattedData(formattedData)
    }
  }, [results, queryStatus, hasData])

  // Reset the shouldRunQuery flag when query is done (success or error)
  useEffect(() => {
    if (shouldRunQuery && (queryStatus === OK || queryStatus === ERROR)) {
      setShouldRunQuery(false)
    }
  }, [queryStatus, shouldRunQuery])

  const hasError = queryStatus === ERROR

  // When the user clicks the "Run Quick Query" button
  const handleRunAiQuickQuery = (formValues) => {
    // Clear previous results immediately when starting a new query
    setLastResults(null)
    setLastFormattedData([])

    setQueryParams(formValues)
    setShouldRunQuery(true)
    setLastQuestion(formValues.question)

    // Add tracking for which questions were asked
    track(TRACKING_EVENTS.QUERY_RUN, formValues)
  }

  // Should only be loading if specifically in a loading state
  const isLoading = queryStatus === LOADING && shouldRunQuery

  // Track Excel download
  const trackExcelDownload = () => {
    track(TRACKING_EVENTS.DOWNLOAD, {
      question: lastQuestion,
    })
  }

  // Create a safe filename from the question
  const getExcelFilename = () => {
    if (!lastQuestion) return "ai-quick-answers-results"

    // Remove invalid filename characters and trim long questions
    const sanitized = lastQuestion
      .replace(/[<>:"/\\|?*\r\n\t]/g, " ") // Replace invalid filename chars with spaces
      .replace(/\s+/g, " ") // Replace multiple spaces with a single space
      .trim()

    // Truncate if too long (keeping it reasonable for a filename)
    const maxLength = 100
    const truncated = sanitized.length > maxLength
      ? sanitized.substring(0, maxLength) + "..."
      : sanitized

    return truncated
  }

  // Check if we have results to download
  const hasDownloadableResults = !isEmpty(lastResults?.records) && !isEmpty(lastFormattedData)

  // Show loading indicator if models or portfolios are still loading
  const status = combineTanStackStatuses([modelsStatus, portfoliosStatus])
  if (status === LOADING) {
    return <Loading />
  }
  return (
    <div className="modules_ai-quick-answers">
      <Paper className="side-panel" square elevation={0}>
        <AIInputForms
          portfolios={portfolios}
          models={models}
          onRunQuery={handleRunAiQuickQuery}
          isLoading={isLoading}
        />
      </Paper>

      <div className="main-content">
        <Paper className="content-paper" elevation={0}>
          {hasError && (
            <Typography color="error" align="center">
              There was an error loading data. Please try again later.
            </Typography>
          )}
          <div className="ai-quick-answers-container">
            <div className="header-container">
              <Typography variant="h5" gutterBottom>
                Quick Answer Results
              </Typography>
              {hasDownloadableResults && (
                <div className="download-button-container">
                  <ExcelDownload
                    data={lastFormattedData}
                    fileName={getExcelFilename()}
                    title="Download results as XLSX"
                    location="AI Quick Answers"
                    track={trackExcelDownload}
                  />
                </div>
              )}
            </div>

            <Divider className="main-divider" />

            <div className="content-container">
              {isLoading ? (
                <div className="loading-container">
                  <CircularProgress />
                </div>
              ) : (
                <div className="results-container">
                  {results?.message && isEmpty(results.records) ? (
                    <Alert severity="info" className="error-alert">
                      <Typography gutterBottom>
                        <b>The results cannot be displayed for question:</b> {queryParams.question}
                      </Typography>
                      <Typography className="error-reason" gutterBottom>
                        <b>Reason</b>: {results.message}
                      </Typography>
                    </Alert>
                  ) : (
                    <AIResultsTable
                      records={lastResults?.records}
                      loading={isLoading}
                      question={lastQuestion}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </Paper>
        <div className="disclaimer">
          {AI_DISCLAIMER}
        </div>
      </div>
    </div>
  )
}
